import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import useAnimateOnScroll from '../hooks/useAnimateOnScroll';

const testimonials = [
  { 
    quote: 'Last 8 months se inki event security team ke saath kaam kar rahe hain. Entry control, guest handling aur emergency response disciplined tha, isi wajah se hamara annual function smoothly conduct hua.', 
    name: 'Rohit Malhotra', 
    role: 'Event Coordinator, Patna',
    gradient: 'from-[#1976D2] to-[#00B8D9]'
  },
  { 
    quote: 'Hamari housing society me guard deployment aur visitor register system implement karne ke baad complaints kaafi kam hui hain. Night shift monitoring bhi consistent hai.', 
    name: 'Neha Verma', 
    role: 'RWA Committee Member',
    gradient: 'from-[#0284C7] to-[#38BDF8]'
  },
  { 
    quote: 'Office premises ke liye inhone guard plus CCTV monitoring ka combined setup diya. Reporting format clear hai aur team ka response time fast rehta hai.', 
    name: 'Aakash Srivastava', 
    role: 'Operations Manager, Private Firm',
    gradient: 'from-[#0D9488] to-[#14B8A6]'
  },
  { 
    quote: 'Night guard service reliable hai aur shift handover properly documented hota hai. Site inspection reports se management ko clear visibility milti hai.', 
    name: 'Kunal Mehta', 
    role: 'Facility Head, Commercial Complex',
    gradient: 'from-[#1976D2] to-[#6366F1]'
  },
  { 
    quote: 'Bank branch ke liye armed guard deployment aur visitor screening process professional tarike se handle kiya gaya. Team punctual aur well-trained hai.', 
    name: 'Pooja Nair', 
    role: 'Branch Admin Officer',
    gradient: 'from-[#0369A1] to-[#0284C7]'
  },
];

const Testimonials = () => {
  const animatedRef = useAnimateOnScroll();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const updateCardsPerPage = () => {
      const width = window.innerWidth;
      if (width < 640) setCardsPerPage(1);
      else if (width < 1024) setCardsPerPage(2);
      else setCardsPerPage(3);
    };
    updateCardsPerPage();
    window.addEventListener('resize', updateCardsPerPage);
    return () => window.removeEventListener('resize', updateCardsPerPage);
  }, []);

  // Auto-slide
  useEffect(() => {
    if (isPaused || testimonials.length <= cardsPerPage) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (testimonials.length - cardsPerPage + 1));
    }, 4500);
    return () => clearInterval(timer);
  }, [isPaused, cardsPerPage]);

  const handlePrev = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - cardsPerPage : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) => 
      prev >= testimonials.length - cardsPerPage ? 0 : prev + 1
    );
  };

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + cardsPerPage);

  return (
    <section
      id="testimonials"
      className="relative pt-10 pb-20 md:pt-12 md:pb-24 px-4 sm:px-6 overflow-hidden w-full"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F4FBFE 60%, #FFFFFF 100%)',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Matching crisp geometric dot pattern overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: 'radial-gradient(#1976D2 0.75px, transparent 0.75px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto w-full" ref={animatedRef}>
        
        {/* ===== HEADER (Matching Style & Compact Gap) ===== */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#EAF7FB] border border-[#2C61B924] text-[#1976D2] text-xs font-bold tracking-[0.14em] uppercase mb-3 px-5 py-2 rounded-full shadow-sm">
            <span className="w-2 h-2 rounded-full bg-[#1976D2]" />
            Testimonials
          </div>

          <h2 className="text-[#001726] font-extrabold tracking-tight leading-[1.15] mb-3 text-3xl sm:text-4xl md:text-5xl">
            Client <span className="text-[#1976D2] italic font-serif font-semibold tracking-normal">Testimonials</span>
          </h2>
          <div className="mx-auto mb-4 h-1 w-20 rounded-full bg-gradient-to-r from-[#6FCEEC] to-[#1976D2]" />
          <p className="text-[#64748B] text-base md:text-lg max-w-xl mx-auto font-normal leading-relaxed">
            Real feedback from residential societies, government bodies, and corporate enterprises who rely on AVISS.
          </p>
        </motion.div>

        {/* ===== 3D CARDS CAROUSEL ===== */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleTestimonials.map((item, index) => (
              <motion.div
                key={`${item.name}-${currentIndex}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="bg-white rounded-[24px] p-7 border border-[#2C61B924] shadow-[0_10px_30px_rgba(0,23,38,0.04)] hover:shadow-[0_20px_45px_rgba(25,118,210,0.14)] hover:border-[#1976D2] transition-all duration-300 hover:-translate-y-2 flex flex-col justify-between relative overflow-hidden"
              >
                {/* 3D Top Neon Strip */}
                <div className={`h-1.5 w-full bg-gradient-to-r ${item.gradient} rounded-full mb-5`} />

                <div>
                  {/* Star Rating Badge & Quote Icon */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1 text-[#F59E0B] text-sm">
                      ★★★★★
                    </div>
                    <span className="text-2xl font-serif text-[#1976D2]/30 leading-none">“</span>
                  </div>
                  
                  {/* Quote Text */}
                  <p className="text-[#334155] text-[0.93rem] leading-[1.75] mb-6 min-h-[95px] font-normal">
                    {item.quote}
                  </p>
                </div>
                
                {/* Author Info */}
                <div className="flex items-center gap-3.5 pt-4 border-t border-[#2C61B918]">
                  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-md`}>
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[#001726] font-bold text-sm m-0 leading-tight">{item.name}</p>
                    <p className="text-[#1976D2] text-xs font-semibold m-0 mt-1">{item.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* ===== NAVIGATION BUTTONS ===== */}
          {testimonials.length > cardsPerPage && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#2C61B924] shadow-md flex items-center justify-center text-[#1976D2] hover:bg-[#EAF7FB] transition-all duration-200 text-2xl z-10 cursor-pointer"
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#2C61B924] shadow-md flex items-center justify-center text-[#1976D2] hover:bg-[#EAF7FB] transition-all duration-200 text-2xl z-10 cursor-pointer"
                aria-label="Next"
              >
                ›
              </button>
            </>
          )}

          {/* ===== DOTS ===== */}
          {testimonials.length > cardsPerPage && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: testimonials.length - cardsPerPage + 1 }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 border-0 p-0 cursor-pointer ${
                    currentIndex === index 
                      ? 'w-8 bg-[#1976D2]' 
                      : 'w-2.5 bg-[#CBD5E1] hover:bg-[#94A3B8]'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;